import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { webFormsService, WebForm, WebFormField } from '@/services/web-forms.service';
import { botFlowsService, BotFlow } from '@/services/bot-flows.service';
import { templatesService, Template } from '@/services/templates.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import {
  Plus,
  Trash2,
  GripVertical,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Eye,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Select } from '@/components/Select';

// ─── Field editor row ─────────────────────────────────────────────────────────

interface FieldRowProps {
  field: WebFormField;
  index: number;
  total: number;
  onChange: (field: WebFormField) => void;
  onRemove: () => void;
  onMove: (dir: 'up' | 'down') => void;
}

function FieldRow({ field, index, total, onChange, onRemove, onMove }: FieldRowProps) {
  const update = (patch: Partial<WebFormField>) => onChange({ ...field, ...patch });

  return (
    <div className="rounded-xl border border-border bg-surface-alt p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <GripVertical size={14} className="text-text-muted shrink-0 cursor-grab" />
        <input
          className="min-w-[10rem] flex-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
          placeholder="Label"
          value={field.label}
          onChange={(e) => update({ label: e.target.value })}
        />
        <input
          className="w-28 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-mono text-text-primary focus:outline-none focus:border-primary"
          placeholder="key"
          value={field.key}
          onChange={(e) =>
            update({ key: e.target.value.toLowerCase().replace(/\s+/g, '_') })
          }
        />
        <Select
          className="w-28 rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
          value={field.type}
          onChange={(e) => update({ type: e.target.value as WebFormField['type'] })}
        >
          <option value="text">Text</option>
          <option value="email">Email</option>
          <option value="tel">Phone</option>
          <option value="textarea">Textarea</option>
          <option value="select">Select</option>
        </Select>
        <label className="flex items-center gap-1 text-xs text-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={!!field.required}
            onChange={(e) => update({ required: e.target.checked })}
          />
          Required
        </label>
        <div className="flex gap-0.5">
          <button
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 rounded text-text-muted hover:text-primary disabled:opacity-30"
          >
            <ChevronUp size={13} />
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={index === total - 1}
            className="p-1 rounded text-text-muted hover:text-primary disabled:opacity-30"
          >
            <ChevronDown size={13} />
          </button>
          <button
            onClick={onRemove}
            className="p-1 rounded text-text-muted hover:text-red-500"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {field.type === 'select' && (
        <div>
          <input
            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
            placeholder="Options (comma-separated, e.g.: Option A, Option B)"
            value={(field.options ?? []).join(', ')}
            onChange={(e) =>
              update({
                options: e.target.value
                  .split(',')
                  .map((o) => o.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
      )}
    </div>
  );
}

// ─── Form preview panel ──────────────────────────────────────────────────────

interface PreviewProps {
  name: string;
  description: string;
  fields: WebFormField[];
}

function FormPreview({ name, description, fields }: PreviewProps) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 space-y-3">
      <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">
        Preview
      </p>
      {name && <h3 className="font-display text-base font-bold text-text-primary">{name}</h3>}
      {description && <p className="text-xs text-text-muted">{description}</p>}
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <label className="block text-xs font-semibold text-text-primary">
            {f.label || f.key}
            {f.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          {f.type === 'textarea' ? (
            <textarea
              disabled
              rows={3}
              placeholder={`Enter ${f.label || f.key}`}
              className="w-full rounded-lg border border-border bg-surface-alt px-2.5 py-1.5 text-xs text-text-muted resize-none"
            />
          ) : f.type === 'select' ? (
            <Select
              disabled
              className="w-full rounded-lg border border-border bg-surface-alt px-2.5 py-1.5 text-xs text-text-muted"
            >
              <option>— Select —</option>
              {(f.options ?? []).map((o) => (
                <option key={o}>{o}</option>
              ))}
            </Select>
          ) : (
            <input
              disabled
              type={f.type}
              placeholder={`Enter ${f.label || f.key}`}
              className="w-full rounded-lg border border-border bg-surface-alt px-2.5 py-1.5 text-xs text-text-muted"
            />
          )}
        </div>
      ))}
      <button
        disabled
        className="w-full py-2 bg-primary text-white rounded-lg text-xs font-semibold opacity-80"
      >
        Submit
      </button>
    </div>
  );
}

// ─── Embed snippet card ───────────────────────────────────────────────────────

interface EmbedCardProps {
  formUid: string;
}

function EmbedCard({ formUid }: EmbedCardProps) {
  const [copied, setCopied] = useState(false);
  const snippet = webFormsService.getEmbedSnippet(formUid);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const testUrl = `${process.env.REACT_APP_API_BASE?.replace('/api/v1', '') ?? ''}/embed/v1.js`;
  const testPageUrl = `data:text/html;charset=utf-8,${encodeURIComponent(
    `<!DOCTYPE html><html><head><title>Test form</title></head><body style="font-family:sans-serif;max-width:520px;margin:40px auto"><h2>Test form</h2>${snippet}</body></html>`,
  )}`;

  return (
    <Card padded className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-text-primary uppercase tracking-wider">
          Embed code
        </p>
        <div className="flex items-center gap-2">
          <a
            href={testPageUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Eye size={12} />
            Test
          </a>
          <Button size="sm" variant="ghost" iconLeft={copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />} onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <pre className="overflow-x-auto rounded-xl bg-surface-alt px-4 py-3 text-[11px] font-mono text-text-secondary leading-relaxed whitespace-pre-wrap break-all">
        {snippet}
      </pre>
      <p className="mt-2 text-xs text-text-muted">
        Public submit URL:{' '}
        <code className="rounded bg-surface px-1 py-0.5 font-mono text-[10px]">
          {webFormsService.getPublicSubmitUrl(formUid)}
        </code>
      </p>

      {/* Hosted form link — for partners who can't embed JS */}
      <div className="mt-3 rounded-xl border border-border bg-surface-alt/40 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">
            Hosted form link
          </span>
          <a
            href={`/embed/forms/${formUid}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline"
          >
            Open
          </a>
        </div>
        <code className="mt-1 block break-all rounded bg-surface px-2 py-1.5 font-mono text-[10.5px] text-text-secondary">
          {(typeof window !== 'undefined' ? window.location.origin : '')}/embed/forms/{formUid}
        </code>
        <p className="mt-1 text-2xs text-text-muted">
          Share this URL anywhere. Works without embedding JavaScript.
        </p>
      </div>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function WebFormBuilder() {
  const navigate = useNavigate();
  const { formUid } = useParams<{ formUid?: string }>();
  const isEdit = !!formUid && formUid !== 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<WebFormField[]>([
    { key: 'name', label: 'Full name', type: 'text', required: true },
    { key: 'phone', label: 'Phone number', type: 'tel', required: true },
    { key: 'email', label: 'Email address', type: 'email', required: false },
  ]);
  const [defaultBotFlowUid, setDefaultBotFlowUid] = useState('');
  const [greetingTemplateUid, setGreetingTemplateUid] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [botFlows, setBotFlows] = useState<BotFlow[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const { active: activeAccount } = useActiveAccount();
  const [saving, setSaving] = useState(false);
  const [savedFormUid, setSavedFormUid] = useState<string | null>(isEdit ? formUid : null);

  useEffect(() => {
    const proms: Promise<unknown>[] = [
      botFlowsService.list().then(setBotFlows),
      templatesService.list({ status: 'APPROVED', limit: 100, accountUid: activeAccount?.accountUid }).then((r) => setTemplates(r.items)),
    ];
    if (isEdit) {
      proms.push(
        webFormsService.get(formUid!).then((form) => {
          setName(form.name);
          setDescription(form.description ?? '');
          setFields(form.fields);
          setDefaultBotFlowUid(form.defaultBotFlowUid ?? '');
          setGreetingTemplateUid(form.greetingTemplateUid ?? '');
          setIsActive(form.isActive);
          setSavedFormUid(form.formUid);
        }),
      );
    }
    Promise.all(proms)
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formUid, isEdit, activeAccount?.accountUid]);

  const addField = () =>
    setFields((prev) => [
      ...prev,
      { key: `field_${prev.length + 1}`, label: `Field ${prev.length + 1}`, type: 'text' as const },
    ]);

  const updateField = (index: number, field: WebFormField) =>
    setFields((prev) => prev.map((f, i) => (i === index ? field : f)));

  const removeField = (index: number) =>
    setFields((prev) => prev.filter((_, i) => i !== index));

  const moveField = (index: number, dir: 'up' | 'down') => {
    const next = [...fields];
    const swap = dir === 'up' ? index - 1 : index + 1;
    [next[index], next[swap]] = [next[swap], next[index]];
    setFields(next);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Form name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        fields,
        defaultBotFlowUid: defaultBotFlowUid || undefined,
        greetingTemplateUid: greetingTemplateUid || undefined,
        isActive,
      };

      let form: WebForm;
      if (isEdit) {
        form = await webFormsService.update(formUid!, payload);
      } else {
        form = await webFormsService.create(payload);
        navigate(`/leads/web-forms/${form.formUid}`, { replace: true });
      }
      setSavedFormUid(form.formUid);
      toast.success(isEdit ? 'Form updated' : 'Form created');
    } catch {
      // interceptor handles
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Topbar title={isEdit ? 'Edit Form' : 'New Form'} breadcrumb="Leads / Web Forms / Builder" />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-10 w-72 rounded" />
          <div className="skeleton h-48 rounded-2xl" />
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={isEdit ? 'Edit Form' : 'New Form'}
        breadcrumb="Leads / Web Forms / Builder"
        right={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/leads/web-forms')}>
              Cancel
            </Button>
            <Button iconLeft={<Save size={14} />} loading={saving} onClick={handleSave}>
              {isEdit ? 'Save changes' : 'Create form'}
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full grid grid-cols-12 gap-6">
        {/* Left — builder */}
        <div className="col-span-8 space-y-5">
          {/* Basic info */}
          <Card padded>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
              Form details
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1">
                  Form name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                  placeholder="e.g. Contact Us"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1">
                  Description <span className="text-text-muted">(optional)</span>
                </label>
                <textarea
                  className="w-full rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary resize-none"
                  rows={2}
                  placeholder="Shown below the form title"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </Card>

          {/* Fields */}
          <Card padded>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Fields
              </p>
              <Button size="sm" variant="ghost" iconLeft={<Plus size={13} />} onClick={addField}>
                Add field
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((f, i) => (
                <FieldRow
                  key={i}
                  field={f}
                  index={i}
                  total={fields.length}
                  onChange={(updated) => updateField(i, updated)}
                  onRemove={() => removeField(i)}
                  onMove={(dir) => moveField(i, dir)}
                />
              ))}
              {fields.length === 0 && (
                <p className="text-xs text-text-muted text-center py-6">
                  No fields yet. Add at least phone or email.
                </p>
              )}
            </div>
          </Card>

          {/* Automation */}
          <Card padded>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
              Automation
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1">
                  Default bot flow
                </label>
                <Select
                  className="w-full rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                  value={defaultBotFlowUid}
                  onChange={(e) => setDefaultBotFlowUid(e.target.value)}
                >
                  <option value="">— None —</option>
                  {botFlows.map((f) => (
                    <option key={f.flowUid} value={f.flowUid}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1">
                  WhatsApp greeting template{' '}
                  <span className="text-text-muted">(sent when phone provided)</span>
                </label>
                <Select
                  className="w-full rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                  value={greetingTemplateUid}
                  onChange={(e) => setGreetingTemplateUid(e.target.value)}
                >
                  <option value="">— None —</option>
                  {templates.map((t) => (
                    <option key={t.templateUid} value={t.templateUid}>
                      {t.name} ({t.language})
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-text-primary">Active</label>
                <button
                  type="button"
                  onClick={() => setIsActive((v) => !v)}
                  className="text-text-muted hover:text-primary transition-colors"
                >
                  {isActive ? (
                    <span className="text-xs text-primary font-medium">Yes</span>
                  ) : (
                    <span className="text-xs text-text-muted">No</span>
                  )}
                </button>
              </div>
            </div>
          </Card>

          {/* Embed snippet — shown after save */}
          {savedFormUid && <EmbedCard formUid={savedFormUid} />}
        </div>

        {/* Right — live preview */}
        <div className="col-span-4">
          <div className="sticky top-6">
            <FormPreview name={name} description={description} fields={fields} />
          </div>
        </div>
      </div>
    </>
  );
}
